import { mapGetters } from "vuex";
import { showErrorSnackBar, showSuccessSnackBar } from "@/utils/SnackBartHelper";
import Utils from '@/scripts/Utils';
import ProposalsService from '@/services/firebase/Proposals/ProposalsService';
import SolutionsServices from '@/services/firebase/SolutionsServices/SolutionsServices';
import BillingOrderService from '@/services/firebase/BillingOrder/BillingOrderService';
import DiagnosticProposalService from '@/services/firebase/DiagnosticProposal/DiagnosticProposalService';

export default {
  name: "Propostas",

  data: () => ({
    is_loading: true,
    proposals: [],
    filteredProposals: [],
    searchField: '',
    searchOptionSelected: 'Cliente',
    searchOptions: [
      'Cliente',
      'Documento',
      'Franqueado',
      'Tipo de Proposta',
      'Status',
      'Origem'
    ],
    showProposalModal: false,
    selectedProposal: null,
    solutionsData: [],
    openBillingOrderViewDialog: false,
    selectedBillingOrderId: null,
    showDiscountModal: false,
    selectedDiscountProposal: null,
    discountFormValid: true,
    discountForm: {
      hasDiscount: false,
      discountType: 'monetary',
      discountValue: null,
    },
    dateStart: null,
    dateEnd: null,
    menuDateStart: false,
    menuDateEnd: false,
    headers: [
      { text: 'ID', value: 'id_doc', sortable: true },
      { text: 'Origem', value: 'proposalType', sortable: true },
      { text: 'Cliente', value: 'leadName', sortable: true },
      { text: 'Documento', value: 'leadDocument', sortable: true },
      { text: 'Franqueado', value: 'franchiseeName', sortable: true },
      { text: 'Quantidade de Produtos', value: 'totalProductsQuantity', sortable: true },
      { text: 'Quantidade de Serviços', value: 'totalServicesQuantity', sortable: true },
      { text: 'Status', value: 'statusFormatted', sortable: true },
      { text: 'Data de Criação', value: 'createdAtFormatted', sortable: true },
      { text: 'Valor Total', value: 'totalInvestment', sortable: true },
      { text: 'Ações', value: 'actions', sortable: false },
    ],
    statusFilter: null,
    statusOptions: [
      { text: 'Todas', value: null },
      { text: 'Pendente', value: 'pending' },
      { text: 'Aceita', value: 'accepted' },
      { text: 'Faturamento Solicitado', value: 'billing_requested' },
      { text: 'Faturada', value: 'billed' },
    ],
    originFilter: null,
    originOptions: [
      { text: 'Todas', value: null },
      { text: 'ProjectsKits', value: 'projects_kits' },
      { text: 'Diagnostics - Projects', value: 'diagnostics_projects' },
      { text: 'Diagnostics - ProjectsKits', value: 'diagnostics_projects_kits' },
      { text: 'WaterScan', value: 'water_scan' },
      { text: 'WaterFix', value: 'water_fix' },
    ],
    proposalTypeOptions: [
      { text: 'Modelo Performance', value: 'model_performance' },
      { text: 'Venda do Projeto', value: 'investment' },
      { text: 'Pré-Fixado', value: 'pre_fixed' },
      { text: 'Gestão', value: 'management' },
    ],
  }),

  computed: {
    ...mapGetters([
      "getCurrentUserData",
      "getCurrentUserRank",
    ]),
    isFranchisee() {
      return this.getCurrentUserRank === "Franqueado";
    },
    isSuperAdmin() {
      return this.getCurrentUserRank === "Super Admin";
    },
    discountSubtotalFormatted() {
      return this.formatCurrency(this.discountSubtotal);
    },
    discountSubtotal() {
      return this.selectedDiscountProposal
        ? this.getProposalSubtotal(this.selectedDiscountProposal)
        : 0;
    },
    discountAmount() {
      if (!this.discountForm.hasDiscount) return 0;
      return this.calculateDiscountAmount(
        this.discountSubtotal,
        this.discountForm.discountType,
        this.discountForm.discountValue
      );
    },
    discountAmountFormatted() {
      return this.formatCurrency(this.discountAmount);
    },
    discountFinalTotal() {
      return Math.max(this.discountSubtotal - this.discountAmount, 0);
    },
    discountFinalTotalFormatted() {
      return this.formatCurrency(this.discountFinalTotal);
    },
    discountValueRules() {
      return [
        v => !this.discountForm.hasDiscount || v !== null && v !== '' || 'Informe o desconto',
        v => {
          if (!this.discountForm.hasDiscount) return true;
          const value = Number(v || 0);
          if (value < 0) return 'Desconto não pode ser negativo';
          if (this.discountForm.discountType === 'percentage' && value > 100) {
            return 'Desconto não pode ser maior que 100%';
          }
          if (this.discountForm.discountType === 'monetary' && value > this.discountSubtotal) {
            return 'Desconto não pode ser maior que o subtotal';
          }
          return true;
        }
      ];
    },
    filteredHeaders() {
      // Se for franqueado, ocultar coluna de franqueado
      if (this.isFranchisee) {
        return this.headers.filter(h => h.value !== 'franchiseeName');
      }
      return this.headers;
    },
    getItems() {
      let items = [...this.filteredProposals];

      // Aplicar filtro de busca
      if (this.searchField) {
        const searchText = Utils.defaultSpace(this.searchField).toLowerCase();
        items = items.filter(item => {
          switch (this.searchOptionSelected) {
            case 'Cliente':
              return Utils.defaultSpace(item.leadName || '').toLowerCase().includes(searchText);
            case 'Documento':
              return Utils.defaultSpace(item.leadDocument || '').toLowerCase().includes(searchText);
            case 'Franqueado':
              return Utils.defaultSpace(item.franchiseeName || '').toLowerCase().includes(searchText);
            case 'Tipo de Proposta':
              return Utils.defaultSpace(item.typeProposalText || '').toLowerCase().includes(searchText);
            case 'Status':
              return Utils.defaultSpace(item.statusFormatted || '').toLowerCase().includes(searchText);
            case 'Origem':
              return Utils.defaultSpace(item.proposalType || '').toLowerCase().includes(searchText);
            default:
              return true;
          }
        });
      }

      // Aplicar filtro de status
      if (this.statusFilter) {
        items = items.filter(item => item.status === this.statusFilter);
      }

      // Aplicar filtro de origem
      if (this.originFilter) {
        if (this.originFilter === 'diagnostics_projects') {
          // Diagnostics do fluxo dos 7 passos (com id_project)
          items = items.filter(item => 
            item.proposalSource === 'diagnostics' && item.id_project
          );
        } else if (this.originFilter === 'diagnostics_projects_kits') {
          // Diagnostics do fluxo simplificado (sem id_project, apenas id_lead)
          items = items.filter(item => 
            item.proposalSource === 'diagnostics' && !item.id_project && item.id_lead
          );
        } else if (this.originFilter === 'projects_kits') {
          // Filtrar por tag origin 'projects_kits' ou por proposalSource 'diagnostic_proposals'
          items = items.filter(item => 
            item.origin === 'projects_kits' || item.proposalSource === 'diagnostic_proposals'
          );
        } else {
          items = items.filter(item => item.proposalSource === this.originFilter);
        }
      }

      // Aplicar filtro de data
      if (this.dateStart || this.dateEnd) {
        items = items.filter(item => {
          // Obter a data de criação da proposta
          let proposalDate;
          if (item.createdAt) {
            if (item.createdAt._seconds !== undefined) {
              proposalDate = new Date(item.createdAt._seconds * 1000);
            } else if (item.createdAt.seconds !== undefined) {
              proposalDate = new Date(item.createdAt.seconds * 1000);
            } else if (typeof item.createdAt === 'number') {
              proposalDate = new Date(item.createdAt);
            } else {
              proposalDate = new Date(item.createdAt);
            }
          } else if (item.created_at) {
            if (item.created_at._seconds !== undefined) {
              proposalDate = new Date(item.created_at._seconds * 1000);
            } else if (item.created_at.seconds !== undefined) {
              proposalDate = new Date(item.created_at.seconds * 1000);
            } else if (typeof item.created_at === 'number') {
              proposalDate = new Date(item.created_at);
            } else {
              proposalDate = new Date(item.created_at);
            }
          } else {
            // Se não tiver data, não incluir no filtro
            return false;
          }

          // Normalizar a data para comparar apenas dia/mês/ano (sem hora)
          proposalDate.setHours(0, 0, 0, 0);
          
          let startDate = null;
          let endDate = null;
          
          if (this.dateStart) {
            startDate = new Date(this.dateStart);
            startDate.setHours(0, 0, 0, 0);
          }
          
          if (this.dateEnd) {
            endDate = new Date(this.dateEnd);
            endDate.setHours(23, 59, 59, 999);
          }

          // Verificar se está no intervalo
          if (startDate && endDate) {
            return proposalDate >= startDate && proposalDate <= endDate;
          } else if (startDate) {
            return proposalDate >= startDate;
          } else if (endDate) {
            return proposalDate <= endDate;
          }
          
          return true;
        });
      }

      return items;
    },
    dateStartFormatted() {
      if (!this.dateStart) return '';
      const d = new Date(this.dateStart);
      return Utils.formatDate(d, '<dd>/<MM>/<YYYY>');
    },
    dateEndFormatted() {
      if (!this.dateEnd) return '';
      const d = new Date(this.dateEnd);
      return Utils.formatDate(d, '<dd>/<MM>/<YYYY>');
    },
  },

  methods: {
    async loadProposals() {
      this.is_loading = true;
      try {
        // Buscar propostas baseado no tipo de usuário
        // Para franqueado, usar o próprio ID do usuário como id_franchisee
        const franchiseeId = this.isFranchisee ? this.getCurrentUserData.id : null;
        let proposals = await ProposalsService.getAllProposals(franchiseeId);

        // Enriquecer com informações de lead e franqueado
        proposals = await ProposalsService.enrichProposalsWithLeadAndFranchisee(proposals);

        // Formatar propostas para exibição
        this.filteredProposals = proposals.map(proposal => {
          const typeProposal = proposal.typeProposal || {};
          const typeProposalText = this.proposalTypeOptions.find(
            opt => opt.value === typeProposal.type
          )?.text || typeProposal.type || 'N/A';

          // Para diagnostics, usar solutionList; para propostas, usar listSolutions
          // Verificar se é um array antes de usar
          const solutions = Array.isArray(proposal.listSolutions) 
            ? proposal.listSolutions 
            : (Array.isArray(proposal.solutionList) ? proposal.solutionList : []);
          const totalInvestmentValue = this.getProposalFinalTotal(proposal);

          // Calcular quantidade total de produtos
          let totalProductsQuantity = 0;
          // Somar quantidades de soluções (consideradas como produtos)
          solutions.forEach(sol => {
            if (sol.quantity) totalProductsQuantity += parseFloat(sol.quantity) || 0;
            else if (sol.amount) totalProductsQuantity += parseFloat(sol.amount) || 0;
            else if (sol.amountSolution) totalProductsQuantity += parseFloat(sol.amountSolution) || 0;
          });
          // Somar quantidades de produtos
          if (Array.isArray(proposal.listProducts)) {
            proposal.listProducts.forEach(prod => {
              if (prod.quantity) totalProductsQuantity += parseFloat(prod.quantity) || 0;
              else if (prod.amount) totalProductsQuantity += parseFloat(prod.amount) || 0;
            });
          }

          // Calcular quantidade total de serviços
          let totalServicesQuantity = 0;
          // Somar quantidades de serviços
          if (Array.isArray(proposal.listServices)) {
            proposal.listServices.forEach(serv => {
              if (serv.quantity) totalServicesQuantity += parseFloat(serv.quantity) || 0;
              else if (serv.amount) totalServicesQuantity += parseFloat(serv.amount) || 0;
            });
          }

          // Formatar data de criação
          let createdAtFormatted = 'N/A';
          if (proposal.createdAt) {
            let createdAt;
            // Lidar com diferentes formatos de data
            if (proposal.createdAt._seconds !== undefined) {
              createdAt = new Date(proposal.createdAt._seconds * 1000);
            } else if (proposal.createdAt.seconds !== undefined) {
              createdAt = new Date(proposal.createdAt.seconds * 1000);
            } else if (typeof proposal.createdAt === 'number') {
              createdAt = new Date(proposal.createdAt);
            } else {
              createdAt = new Date(proposal.createdAt);
            }
            createdAtFormatted = Utils.formatDate(createdAt, '<dd>/<MM>/<YYYY>');
          } else if (proposal.created_at) {
            // Fallback para created_at
            let createdAt;
            if (proposal.created_at._seconds !== undefined) {
              createdAt = new Date(proposal.created_at._seconds * 1000);
            } else if (proposal.created_at.seconds !== undefined) {
              createdAt = new Date(proposal.created_at.seconds * 1000);
            } else if (typeof proposal.created_at === 'number') {
              createdAt = new Date(proposal.created_at);
            } else {
              createdAt = new Date(proposal.created_at);
            }
            createdAtFormatted = Utils.formatDate(createdAt, '<dd>/<MM>/<YYYY>');
          }

          // Formatar status
          const statusMap = {
            'pending': 'Pendente',
            'accepted': 'Aceita',
            'billing_requested': 'Faturamento Solicitado',
            'billed': 'Faturada'
          };
          const statusFormatted = statusMap[proposal.status] || proposal.status || 'Pendente';

          return {
            ...proposal,
            typeProposalText,
            totalInvestment: this.formatCurrency(totalInvestmentValue),
            totalInvestmentValue,
            subtotalInvestment: this.getProposalSubtotal(proposal),
            discountAmountValue: this.getProposalDiscountAmount(proposal),
            totalProductsQuantity: totalProductsQuantity,
            totalServicesQuantity: totalServicesQuantity,
            createdAtFormatted,
            statusFormatted,
            leadDocument: proposal.leadDocument || 'N/A',
          };
        });

        // Ordenar por data de criação (mais recente primeiro)
        this.filteredProposals.sort((a, b) => {
          const dateA = a.createdAt?._seconds 
            ? new Date(a.createdAt._seconds * 1000)
            : new Date(a.createdAt || 0);
          const dateB = b.createdAt?._seconds 
            ? new Date(b.createdAt._seconds * 1000)
            : new Date(b.createdAt || 0);
          return dateB - dateA;
        });
      } catch (error) {
        console.error('Error loading proposals:', error);
        showErrorSnackBar('Erro ao carregar propostas');
      } finally {
        this.is_loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '';
      if (date._seconds) {
        return Utils.formatDate(new Date(date._seconds * 1000), '<dd>/<MM>/<YYYY>');
      }
      return Utils.formatDate(new Date(date), '<dd>/<MM>/<YYYY>');
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    },
    getProposalSubtotal(proposal) {
      const calculateListTotal = (list) => {
        if (!Array.isArray(list)) return 0;
        return list.reduce((sum, item) => {
          const isSelected = item.selected !== false;
          if (!isSelected) return sum;
          return sum + (Number(item.total) || 0);
        }, 0);
      };

      const solutions = Array.isArray(proposal.listSolutions)
        ? proposal.listSolutions
        : (Array.isArray(proposal.solutionList) ? proposal.solutionList : []);

      return calculateListTotal(solutions)
        + calculateListTotal(proposal.listServices)
        + calculateListTotal(proposal.listProducts);
    },
    calculateDiscountAmount(subtotal, discountType, discountValue) {
      const value = Number(discountValue || 0);
      if (value <= 0) return 0;
      if (discountType === 'percentage') return subtotal * value / 100;
      if (discountType === 'monetary') return value;
      return 0;
    },
    getProposalDiscountAmount(proposal) {
      if (!proposal?.typeProposal?.hasDiscount) return 0;
      const subtotal = this.getProposalSubtotal(proposal);
      const discount = this.calculateDiscountAmount(
        subtotal,
        proposal.typeProposal.discountType,
        proposal.typeProposal.discountValue
      );
      return Math.min(discount, subtotal);
    },
    getProposalFinalTotal(proposal) {
      const subtotal = this.getProposalSubtotal(proposal);
      const discount = this.getProposalDiscountAmount(proposal);
      return Math.max(subtotal - discount, 0);
    },
    canAddDiscount(item) {
      return this.isSuperAdmin && (item.status || 'pending') === 'pending';
    },
    openDiscountModal(item) {
      this.selectedDiscountProposal = item;
      this.discountForm = {
        hasDiscount: !!item.typeProposal?.hasDiscount,
        discountType: item.typeProposal?.discountType || 'monetary',
        discountValue: item.typeProposal?.discountValue ?? null,
      };
      this.showDiscountModal = true;
      this.$nextTick(() => {
        if (this.$refs.discountForm) this.$refs.discountForm.resetValidation();
      });
    },
    closeDiscountModal() {
      this.showDiscountModal = false;
      this.selectedDiscountProposal = null;
      this.discountForm = {
        hasDiscount: false,
        discountType: 'monetary',
        discountValue: null,
      };
    },
    async saveDiscount() {
      if (!this.selectedDiscountProposal?.id_doc) {
        showErrorSnackBar('Proposta não encontrada.');
        return;
      }

      if (!this.canAddDiscount(this.selectedDiscountProposal)) {
        showErrorSnackBar('Descontos só podem ser adicionados em propostas pendentes.');
        return;
      }

      if (this.$refs.discountForm && !this.$refs.discountForm.validate()) {
        return;
      }

      try {
        this.is_loading = true;
        const updatedTypeProposal = {
          ...(this.selectedDiscountProposal.typeProposal || {}),
          hasDiscount: !!this.discountForm.hasDiscount,
          discountType: this.discountForm.hasDiscount ? this.discountForm.discountType : null,
          discountValue: this.discountForm.hasDiscount ? Number(this.discountForm.discountValue || 0) : null,
        };

        await DiagnosticProposalService.editProposal(this.selectedDiscountProposal.id_doc, {
          typeProposal: updatedTypeProposal,
          id_doc: this.selectedDiscountProposal.id_doc,
        });

        showSuccessSnackBar('Desconto salvo com sucesso!');
        this.closeDiscountModal();
        await this.loadProposals();
      } catch (error) {
        console.error('Erro ao salvar desconto:', error);
        showErrorSnackBar('Erro ao salvar desconto. Tente novamente.');
      } finally {
        this.is_loading = false;
      }
    },
    viewProposal(item) {
      // Abrir modal com visualização simplificada
      this.selectedProposal = item;
      this.showProposalModal = true;
    },
    closeProposalModal() {
      this.showProposalModal = false;
      this.selectedProposal = null;
    },
    navigateToProposal(item) {
      // Navegar para a proposta completa baseado na origem
      if (item.proposalSource === 'diagnostic_proposals') {
        // Proposta de ProjectsKits - navegar para o projeto
        if (item.id_lead) {
          this.$router.push(`/projects-kits?leadId=${item.id_lead}&proposalId=${item.id_doc}`);
        }
      } else if (item.proposalSource === 'diagnostics') {
        // Diagnostic - diferenciar entre fluxo dos 7 passos e fluxo simplificado
        if (item.id_project) {
          // Fluxo dos 7 passos (Projects) - navegar para o projeto
          this.$router.push(`/projects/${item.id_project}`);
        } else if (item.id_lead) {
          // Fluxo simplificado (ProjectsKits) - navegar para projects-kits
          this.$router.push(`/projects-kits?leadId=${item.id_lead}&diagnosticId=${item.id_doc}`);
        }
      } else if (item.proposalSource === 'water_scan') {
        // Proposta WaterScan
        if (item.id_doc) {
          this.$router.push(`/water-scan/consult-proposal/${item.id_doc}`);
        }
      } else if (item.proposalSource === 'water_fix') {
        // Proposta WaterFix
        if (item.registration) {
          this.$router.push(`/water-fix/consult-proposal/${item.registration}`);
        }
      }
      this.closeProposalModal();
    },
    getSolutionName(solutionId) {
      // Buscar nome da solução pelo ID
      if (!solutionId) return 'N/A';
      const solution = this.solutionsData.find(s => s.id === solutionId || s.id === String(solutionId));
      return solution ? (solution.solution || solution.name || 'N/A') : `ID: ${solutionId}`;
    },
    getProposalItems(proposal) {
      // Retornar lista de itens (soluções, produtos e serviços) para exibição
      const items = [];
      
      // Soluções
      const solutions = Array.isArray(proposal.listSolutions) 
        ? proposal.listSolutions 
        : (Array.isArray(proposal.solutionList) ? proposal.solutionList : []);
      
      solutions.forEach(sol => {
        let solutionName = sol.solution || sol.solutionName;
        // Se não tiver nome, buscar pelo ID
        if (!solutionName && sol.solutionId) {
          solutionName = this.getSolutionName(sol.solutionId);
        } else if (!solutionName && sol.id) {
          solutionName = this.getSolutionName(sol.id);
        }
        
        if (solutionName || sol.solution || sol.solutionName || sol.solutionId || sol.id) {
          items.push({
            name: solutionName || 'Solução',
            quantity: parseFloat(sol.quantity || sol.amount || sol.amountSolution || 0),
            unitPrice: parseFloat(sol.unitPrice || sol.price || 0),
            total: parseFloat(sol.total || 0),
            type: 'Solução'
          });
        }
      });
      
      // Produtos
      if (Array.isArray(proposal.listProducts)) {
        proposal.listProducts.forEach(prod => {
          let productName = prod.productName;
          // Se não tiver productName, verificar se product é um ID ou nome
          if (!productName && prod.product) {
            // Se product parece ser um ID (número ou string que pode ser convertida para número)
            const isId = typeof prod.product === 'number' || 
                        (typeof prod.product === 'string' && /^\d+$/.test(prod.product));
            
            if (isId) {
              // Buscar nome pelo ID
              productName = this.getSolutionName(prod.product);
            } else {
              // Se não for ID, usar o próprio valor como nome
              productName = prod.product;
            }
          }
          
          if (productName || prod.product) {
            items.push({
              name: productName || 'Produto',
              quantity: parseFloat(prod.quantity || prod.amount || 0),
              unitPrice: parseFloat(prod.unitPrice || prod.price || 0),
              total: parseFloat(prod.total || 0),
              type: 'Produto'
            });
          }
        });
      }
      
      // Serviços
      if (Array.isArray(proposal.listServices)) {
        proposal.listServices.forEach(serv => {
          let serviceName = serv.serviceName;
          // Se não tiver serviceName, verificar se service é um ID ou nome
          if (!serviceName && serv.service) {
            // Se service parece ser um ID (número ou string que pode ser convertida para número)
            const isId = typeof serv.service === 'number' || 
                        (typeof serv.service === 'string' && /^\d+$/.test(serv.service));
            
            if (isId) {
              // Buscar nome pelo ID
              serviceName = this.getSolutionName(serv.service);
            } else {
              // Se não for ID, usar o próprio valor como nome
              serviceName = serv.service;
            }
          }
          
          if (serviceName || serv.service) {
            items.push({
              name: serviceName || 'Serviço',
              quantity: parseFloat(serv.quantity || serv.amount || 0),
              unitPrice: parseFloat(serv.unitPrice || serv.price || 0),
              total: parseFloat(serv.total || 0),
              type: 'Serviço'
            });
          }
        });
      }
      
      return items;
    },
    async loadSolutionsData() {
      try {
        this.solutionsData = await SolutionsServices.GetSolutions();
      } catch (error) {
        console.error('Erro ao carregar soluções:', error);
        this.solutionsData = [];
      }
    },
    clearDateFilters() {
      this.dateStart = null;
      this.dateEnd = null;
    },
    formatDateForDisplay(date) {
      if (!date) return '';
      const d = new Date(date);
      return Utils.formatDate(d, '<dd>/<MM>/<YYYY>');
    },
    viewBillingOrder(item) {
      if (!item.billingOrderId) {
        showErrorSnackBar('Esta proposta não possui ordem de faturamento vinculada.');
        return;
      }
      this.selectedBillingOrderId = item.billingOrderId;
      this.openBillingOrderViewDialog = true;
    },
    closeBillingOrderViewDialog() {
      this.openBillingOrderViewDialog = false;
      this.selectedBillingOrderId = null;
    },
    async markAsBilled(item) {
      if (item.status !== 'billing_requested') {
        showErrorSnackBar('Apenas propostas com status "Faturamento Solicitado" podem ser marcadas como faturadas.');
        return;
      }

      // Confirmar ação
      if (!confirm(`Deseja realmente marcar a proposta ${item.id_doc} como faturada?`)) {
        return;
      }

      try {
        this.is_loading = true;
        
        // Atualizar status para 'billed'
        await BillingOrderService.updateProposalToBilled(
          item.id_doc,
          item
        );
        
        showSuccessSnackBar('Proposta marcada como faturada com sucesso!');
        
        // Recarregar lista de propostas
        await this.loadProposals();
      } catch (error) {
        console.error('Erro ao marcar proposta como faturada:', error);
        showErrorSnackBar('Erro ao marcar proposta como faturada. Tente novamente.');
      } finally {
        this.is_loading = false;
      }
    },
  },

  async created() {
    await this.loadSolutionsData();
    await this.loadProposals();
  },
};

